
import Link from 'next/link'
import { Icon } from '@/components/ui/Icon'
import { SIGNAL_META } from '@/lib/signals'
import { CATEGORY_META, type DeviceCategory } from '@/lib/categories'
import { btn, card, cx, notice, pageTitle } from '@/lib/ui'
import { formatVa } from '@/lib/power'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import { term } from '@/lib/ui-mode'
import type { RunSheet, RunSheetRow } from '@/lib/run-sheet'

/**
 * The on-site view: a table on a laptop, one card per cable on a phone, and
 * a print stylesheet so the browser's own "Print" produces a usable sheet.
 */
export function RunSheetView({
  sheet,
  power,
  basePath,
  apiBase,
  shared = false,
}: {
  sheet: RunSheet
  /** The power plan for this project, when there is one. */
  power: { id: string; vaRequired: number; unknownRows: number } | null
  basePath: string
  apiBase: string
  /**
   * Rendered from a read-only share link, with no session. Everything that
   * would lead further into the workspace comes off: the canvas, the power
   * plan editor and the two export endpoints, all of which need auth.
   */
  shared?: boolean
}) {
  const { advanced } = useUiMode()
  const generated = new Date(sheet.generatedAt)
  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-6 sm:px-8 sm:py-8 print:max-w-none print:px-0 print:py-0">
      <div className="flex flex-wrap items-start justify-between gap-4 print:hidden">
        <div>
          {shared ? (
            <p className="inline-flex items-center gap-1 text-xs text-muted">
              <Icon name="eye" className="h-3.5 w-3.5" />
              Shared, read only
            </p>
          ) : (
            <Link
              href={`${basePath}/projects/${sheet.project.id}`}
              className="inline-flex items-center gap-1 text-xs text-muted transition-colors hover:text-foreground"
            >
              <Icon name="back" className="h-3.5 w-3.5" />
              Back to canvas
            </Link>
          )}
          <h1 className={cx(pageTitle, 'mt-2')}>{sheet.project.name}</h1>
          <p className="mt-1.5 text-sm text-muted">
            {term(advanced, 'runSheet')} · {sheet.totals.cables} cable{sheet.totals.cables === 1 ? '' : 's'} ·{' '}
            {sheet.totals.devices} device{sheet.totals.devices === 1 ? '' : 's'} · generated{' '}
            {generated.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
          {/* The crew already prints this page, so the supply figure belongs
              beside the patch list rather than one navigation away. */}
          {power && shared && (
            <p className="mt-1.5 inline-flex items-center gap-1 text-sm text-muted">
              <Icon name="plug" className="h-3.5 w-3.5" />
              Power: {formatVa(power.vaRequired)} required
              {power.unknownRows > 0 &&
                ` · ${power.unknownRows} ${power.unknownRows === 1 ? 'row' : 'rows'} without a figure`}
            </p>
          )}
          {power && !shared && (
            <Link
              href={`${basePath}/power/${power.id}`}
              className="mt-1.5 inline-flex items-center gap-1 text-sm text-muted transition-colors hover:text-foreground"
            >
              <Icon name="plug" className="h-3.5 w-3.5" />
              Power plan: {formatVa(power.vaRequired)} required
              {power.unknownRows > 0 &&
                ` · ${power.unknownRows} ${power.unknownRows === 1 ? 'row' : 'rows'} without a figure`}
              <Icon name="arrowRight" className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!shared && (
            <>
              <a href={`${apiBase}/projects/${sheet.project.id}/run-sheet.csv`} className={cx(btn.base, btn.secondary)}>
                <Icon name="download" className="h-4 w-4" />
                CSV
              </a>
              <a href={`${apiBase}/projects/${sheet.project.id}/run-sheet.pdf`} className={cx(btn.base, btn.secondary)}>
                <Icon name="download" className="h-4 w-4" />
                PDF
              </a>
            </>
          )}
          <button type="button" onClick={() => window.print()} className={cx(btn.base, btn.primary)}>
            <Icon name="print" className="h-4 w-4" />
            Print
          </button>
        </div>
      </div>

      <div className="hidden print:block">
        <h1 className="text-lg font-semibold">{sheet.project.name} — {term(advanced, 'runSheet')}</h1>
        <p className="text-xs">
          {sheet.workspace.name} · {sheet.totals.cables} cables · {sheet.totals.devices} devices ·{' '}
          {generated.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </p>
      </div>

      {sheet.sections.length === 0 && (
        <div className={cx(card, 'gradient-hero mt-8 px-6 py-14 text-center print:border-0')}>
          <p className="text-sm text-muted">No cables on this canvas yet. Patch some ports and come back.</p>
        </div>
      )}

      {sheet.sections.map((section) => (
        <section key={section.signal} className="mt-8 break-inside-avoid print:mt-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.08em]">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 rounded-full print:hidden"
              style={{ backgroundColor: SIGNAL_META[section.signal].color }}
            />
            {section.label}
            <span className="rounded-full bg-hover px-2 py-0.5 font-mono text-[11px] font-normal normal-case tracking-normal text-muted print:hidden">
              {section.rows.length} cable{section.rows.length === 1 ? '' : 's'}
            </span>
          </h2>

          {/* Table: tablets, laptops, print */}
          <div className={cx(card, 'mt-3 hidden overflow-x-auto md:block print:block print:border-0 print:shadow-none')}>
            <table className="w-full text-sm print:text-[11px]">
              <thead className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
                <tr>
                  {advanced && <th className="px-4 py-2.5 font-medium">Cable</th>}
                  <th className="px-2 py-2.5 font-medium">From device</th>
                  {advanced && <th className="px-2 py-2.5 font-medium">Unit</th>}
                  <th className="px-2 py-2.5 font-medium">Port</th>
                  <th className="px-2 py-2.5 font-medium">Connector</th>
                  <th className="px-1 py-2.5" />
                  <th className="px-2 py-2.5 font-medium">Port</th>
                  <th className="px-2 py-2.5 font-medium">Connector</th>
                  <th className="px-2 py-2.5 font-medium">To device</th>
                  {advanced && <th className="px-2 py-2.5 font-medium">Unit</th>}
                  <th className="px-4 py-2.5 text-right font-medium">Length</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row, index) => (
                  <tr key={row.cableId + index} className="border-t border-[var(--border)] print:border-slate-300">
                    {advanced && (
                      <td className="whitespace-nowrap px-4 py-2 font-mono font-medium" style={{ color: SIGNAL_META[row.signal].text }}>
                        {row.cableId}
                        {row.cableIdIsAuto && <span className="ml-1.5 rounded-full bg-hover px-1.5 text-[9px] font-normal text-muted print:hidden" title="Generated — set a cable ID on the canvas to fix it">auto</span>}
                      </td>
                    )}
                    <td className="px-2 py-2">
                      <DeviceName name={row.fromDevice} category={row.fromCategory} />
                      <span className="block text-[11px] text-muted">{row.fromModel}</span>
                    </td>
                    {advanced && (
                      <td className="px-2 py-2 font-mono text-xs text-muted">{row.fromUnit ?? '—'}</td>
                    )}
                    <td className="px-2 py-2">
                      {row.fromPort}
                      {row.fromSlot && <span className="block text-[11px] text-muted">{row.fromSlot}</span>}
                    </td>
                    <td className="px-2 py-2 text-muted">{row.fromConnector}</td>
                    <td className="px-1 py-2 text-muted">→</td>
                    <td className="px-2 py-2">
                      {row.toPort}
                      {row.toSlot && <span className="block text-[11px] text-muted">{row.toSlot}</span>}
                    </td>
                    <td className="px-2 py-2 text-muted">{row.toConnector}</td>
                    <td className="px-2 py-2">
                      <DeviceName name={row.toDevice} category={row.toCategory} />
                      <span className="block text-[11px] text-muted">{row.toModel}</span>
                    </td>
                    {advanced && (
                      <td className="px-2 py-2 font-mono text-xs text-muted">{row.toUnit ?? '—'}</td>
                    )}
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-muted">
                      {row.lengthM == null ? '—' : `${row.lengthM} m`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards: phones */}
          <ul className="mt-3 space-y-2 md:hidden print:hidden">
            {section.rows.map((row, index) => (
              <CableCard key={row.cableId + index} row={row} />
            ))}
          </ul>
        </section>
      ))}

      {sheet.unpatchedDevices.length > 0 && (
        <p className={cx('mt-8 text-xs', notice.warning, 'print:border-slate-300 print:bg-transparent print:text-black')}>
          Placed but not patched: {sheet.unpatchedDevices.join(', ')}
        </p>
      )}
      <p className="mt-4 text-[11px] text-muted print:hidden">
        Cable IDs marked “auto” are numbered in sheet order. Select a cable on the canvas to give it a fixed ID.
      </p>
    </div>
  )
}

function CableCard({ row }: { row: RunSheetRow }) {
  return (
    <li className={cx(card, 'p-4')}>
      <p className="flex items-center justify-between">
        <span className="font-mono text-sm font-semibold" style={{ color: SIGNAL_META[row.signal].text }}>
          {row.cableId}
        </span>
        <span className="rounded-full bg-hover px-2 py-0.5 font-mono text-[10px] uppercase text-muted">{row.fromConnector}</span>
      </p>
      <div className="mt-2.5 grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
        <div className="min-w-0">
          <DeviceName name={row.fromDevice} category={row.fromCategory} />
          <p className="truncate text-xs text-muted">{row.fromPort}</p>
          {row.fromSlot && <p className="truncate text-[10px] text-muted">{row.fromSlot}</p>}
          {row.fromUnit && <p className="truncate font-mono text-[10px] text-muted">{row.fromUnit}</p>}
        </div>
        <Icon name="arrowRight" className="h-4 w-4 text-muted" />
        <div className="min-w-0 text-right">
          <DeviceName name={row.toDevice} category={row.toCategory} align="right" />
          <p className="truncate text-xs text-muted">{row.toPort}</p>
          {row.toSlot && <p className="truncate text-[10px] text-muted">{row.toSlot}</p>}
          {row.toUnit && <p className="truncate font-mono text-[10px] text-muted">{row.toUnit}</p>}
          <p className="mt-0.5 font-mono text-[10px] uppercase text-muted">{row.toConnector}</p>
        </div>
      </div>
      {/* Length and the far-end connector are what someone actually packs by,
          and the phone layout dropped both — leaving the card unusable for the
          job the landing page says it is for. */}
      <p className="mt-2.5 border-t border-[var(--border)] pt-2 font-mono text-[10px] uppercase text-muted">
        {row.lengthM === null ? 'No length set' : `${row.lengthM} m`}
      </p>
    </li>
  )
}

/** A device name with its category glyph, the same cue the canvas and library use. */
function DeviceName({ name, category, align = 'left' }: { name: string; category: DeviceCategory; align?: 'left' | 'right' }) {
  return (
    <span className={cx('flex items-center gap-1.5 font-medium', align === 'right' && 'flex-row-reverse text-right')}>
      <Icon
        name={CATEGORY_META[category].icon}
        strokeWidth={2}
        className="h-3.5 w-3.5 shrink-0 text-muted print:hidden"
        aria-label={CATEGORY_META[category].singular}
      />
      <span className="min-w-0 truncate">{name}</span>
    </span>
  )
}
